import { useEffect, useRef, useState } from "react"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import { uploadFileWithProgress } from "../../lib/uploadWithProgress"
import { enqueueTranscription, useTranscriptionState } from "../../lib/transcriptionQueue"
import { labelCls, inputCls, btnSecondary } from "./FormComponents"
import { Toggle } from "../ui/Toggle"
import { PlusIcon, CloseIcon, CheckCircleIcon, TrashBinIcon } from "../../icons"
import { LoadState } from "@/components/ui/LoadState"
import { ConfirmDialog } from "./ConfirmDialog"

type TrackStatus = "uploading" | "processing" | "ready" | "error"

type AudioTrack = {
  id: string
  language_code: string
  label: string
  is_original: boolean
  source_type: "video" | "upload" | "reuses_original"
  status: TrackStatus
  has_captions: boolean
  error_message: string | null
}

const STATUS_LABELS: Record<TrackStatus, string> = {
  uploading: "Subiendo",
  processing: "Procesando",
  ready: "Listo",
  error: "Error",
}

const COMMON_LANGUAGES = [
  { code: "es", label: "Español" },
  { code: "en", label: "Inglés" },
  { code: "pt", label: "Portugués" },
  { code: "fr", label: "Francés" },
]

function StatusBadge({ status }: { status: TrackStatus }) {
  const styles: Record<TrackStatus, string> = {
    uploading: "bg-gray-100 text-gray-500 dark:bg-white/[0.05] dark:text-gray-400",
    processing: "bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400",
    ready: "bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400",
    error: "bg-error-50 text-error-600 dark:bg-error-500/10 dark:text-error-400",
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[status]}`}>
      {status === "ready" && <CheckCircleIcon className="size-3" />}
      {STATUS_LABELS[status]}
    </span>
  )
}

const TRANSCRIPTION_LABELS = {
  queued: "En cola para transcribir",
  transcribing: "Transcribiendo en tu navegador…",
  done: "Subtítulos generados",
  error: "La transcripción falló",
} as const

function AudioTrackRow({
  track,
  uploadPercent,
  onDelete,
}: {
  track: AudioTrack
  uploadPercent: number | undefined
  onDelete: () => void
}) {
  // Estado reactivo de la cola de transcripción (ver lib/transcriptionQueue) —
  // se actualiza sin recargar la lista de tracks, incluso si el admin navegó
  // aquí desde otra página mientras corría en segundo plano. Los tracks
  // reuses_original nunca se encolan, así que su estado siempre es undefined.
  const transcriptionState = useTranscriptionState(track.id)

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3 dark:border-gray-700 dark:bg-white/[0.02]">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">
            {track.label} <span className="font-normal text-gray-400">({track.language_code})</span>
          </p>
          {track.is_original && (
            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">
              Original
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <StatusBadge status={track.status} />
          {track.source_type === "reuses_original" && <span>Usa el audio original</span>}
          {track.has_captions && <span className="text-success-600 dark:text-success-400">Subtítulos listos</span>}
          {!track.has_captions && transcriptionState && (
            <span className={transcriptionState === "error" ? "text-error-600 dark:text-error-400" : ""}>
              {TRANSCRIPTION_LABELS[transcriptionState]}
            </span>
          )}
          {uploadPercent !== undefined && <span>Subiendo… {uploadPercent}%</span>}
        </div>
        {track.status === "error" && track.error_message && (
          <p className="mt-1 text-xs text-error-600 dark:text-error-400">{track.error_message}</p>
        )}
      </div>
      {!track.is_original && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Eliminar track ${track.label}`}
          className="flex size-11 shrink-0 items-center justify-center rounded-lg text-gray-500 sm:size-8 hover:bg-error-50 hover:text-error-500 dark:text-gray-400 dark:hover:bg-error-500/10"
        >
          <TrashBinIcon className="size-4" />
        </button>
      )}
    </div>
  )
}

/**
 * Tab "Audio" del gestor de video — un track por idioma, cada uno con audio
 * propio o marcado para usar el audio original. Ver
 * openspec/changes/video-multi-audio-tracks.
 */
export default function AudioTracksPanel({ videoId }: { videoId: string }) {
  const [tracks, setTracks] = useState<AudioTrack[]>([])
  const [trackToDelete, setTrackToDelete] = useState<AudioTrack | null>(null)
  const [loading, setLoading] = useState(true)
  const [addingOpen, setAddingOpen] = useState(false)
  const [newLanguageCode, setNewLanguageCode] = useState("en")
  const [newLabel, setNewLabel] = useState("Inglés")
  const [newUseOriginal, setNewUseOriginal] = useState(false)
  const [newFile, setNewFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [uploadPercentByTrack, setUploadPercentByTrack] = useState<Record<string, number>>({})
  const xhrRef = useRef<XMLHttpRequest | null>(null)

  function loadTracks() {
    setLoading(true)
    api
      .get(`/academy/admin/videos/${videoId}`)
      .then((res) => setTracks(res.data?.data?.audio_tracks ?? []))
      .catch((err) => toastError(err))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadTracks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId])

  function resetAddForm() {
    setAddingOpen(false)
    setNewLanguageCode("en")
    setNewLabel("Inglés")
    setNewUseOriginal(false)
    setNewFile(null)
  }

  async function handleAddLanguage() {
    if (!newUseOriginal && !newFile) {
      toastError("Selecciona un archivo de audio o marca \"usar audio original\".")
      return
    }
    setSubmitting(true)
    try {
      const res = await api.post(`/academy/admin/videos/${videoId}/audio-tracks`, {
        language_code: newLanguageCode,
        label: newLabel,
        use_original: newUseOriginal,
        content_type: newFile?.type || "audio/mpeg",
      })
      const { track_id, upload_url, upload_headers } = res.data.data

      if (!newUseOriginal && upload_url && newFile) {
        setUploadPercentByTrack((m) => ({ ...m, [track_id]: 0 }))
        await uploadFileWithProgress(
          upload_url,
          upload_headers,
          newFile,
          (percent) => setUploadPercentByTrack((m) => ({ ...m, [track_id]: percent })),
          xhrRef,
        )
        setUploadPercentByTrack((m) => {
          const rest = { ...m }
          delete rest[track_id]
          return rest
        })
        // Dispara la transcripción en el navegador apenas termina de subirse
        // — no espera a que el pipeline de video termine de procesar el
        // track (ver design.md §5).
        enqueueTranscription({ videoId, trackId: track_id, file: newFile })
      }

      // Un track nuevo (con archivo propio o reuses_original) no existe para
      // el reproductor hasta que el pipeline vuelve a correr y lo agrega al
      // master.m3u8 — si el video ya estaba "ready", hay que volver a
      // encolar el procesamiento explícitamente; agregar el registro solo no
      // alcanza. Re-encola aunque re-codifique de más los renditions de
      // video existentes: es correcto, aunque no óptimo (ver tasks.md §4.2).
      await api.post(`/academy/admin/videos/${videoId}/process`).catch(() => {
        // Si esto falla, el track queda "esperando" — el admin puede ver el
        // estado y reintentar reprocesando el video manualmente más tarde.
      })

      toastSuccess("Idioma agregado.")
      resetAddForm()
      loadTracks()
    } catch (err) {
      toastError(err)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(trackId: string) {
    try {
      await api.delete(`/academy/admin/videos/${videoId}/audio-tracks/${trackId}`)
      // Mismo motivo que al agregar: el manifiesto necesita regenerarse sin
      // el track eliminado.
      await api.post(`/academy/admin/videos/${videoId}/process`).catch(() => {})
      toastSuccess("Track eliminado.")
      loadTracks()
    } catch (err) {
      toastError(err)
    }
  }

  if (loading) {
    return <LoadState label="Cargando tracks de audio..." />
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {tracks.map((track) => (
          <AudioTrackRow
            key={track.id}
            track={track}
            uploadPercent={uploadPercentByTrack[track.id]}
            onDelete={() => setTrackToDelete(track)}
          />
        ))}
      </div>

      <ConfirmDialog
        isOpen={trackToDelete !== null}
        title="¿Eliminar este track de audio?"
        message="El video se volverá a procesar sin este idioma. No se puede deshacer."
        confirmLabel="Eliminar track"
        onConfirm={() => {
          const id = trackToDelete?.id
          setTrackToDelete(null)
          if (id) void handleDelete(id)
        }}
        onCancel={() => setTrackToDelete(null)}
      />

      {addingOpen ? (
        <div className="flex flex-col gap-3 rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">Agregar idioma</h4>
            <button type="button" onClick={resetAddForm} aria-label="Cerrar" className="text-gray-400 hover:text-gray-600">
              <CloseIcon className="size-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Idioma</label>
              <select
                value={newLanguageCode}
                onChange={(e) => {
                  const code = e.target.value
                  setNewLanguageCode(code)
                  const match = COMMON_LANGUAGES.find((l) => l.code === code)
                  if (match) setNewLabel(match.label)
                }}
                className={inputCls}
              >
                {COMMON_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label} ({l.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Etiqueta</label>
              <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className={inputCls} />
            </div>
          </div>

          <Toggle
            checked={newUseOriginal}
            onChange={setNewUseOriginal}
            label="Usar el audio original para este idioma (sin archivo propio)"
          />

          {!newUseOriginal && (
            <div>
              <label className={labelCls}>Archivo de audio</label>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setNewFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-brand-600 dark:text-gray-300 dark:file:bg-brand-500/10 dark:file:text-brand-300"
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={resetAddForm} disabled={submitting} className={`${btnSecondary} disabled:opacity-50`}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleAddLanguage}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Agregando..." : "Agregar"}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAddingOpen(true)}
          className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300"
        >
          <PlusIcon className="size-3.5" />
          Agregar idioma
        </button>
      )}
    </div>
  )
}
